<template>
  <div class="add-global-notification">
    <editor-header>
      <template slot="header">
        <span>{{ AddGlobalNotificationCaption }}</span>
      </template>

      <template slot="buttons">
        <header-icon-button
          iconName="fa-times"
          test-id="addGlobalNotification.closeButton"
          @clicked="closeButtonClicked"
        />
      </template>
    </editor-header>

    <editor-content>
      <text-area-input
        class="mb-2"
        :readonly="false"
        :label="TextCaption"
        :disabled="isLoading"
        :invalid="textValidationFailed"
        placeholder="Enter message text..."
        :labelWidth="labelWidth"
        :value.sync="text"
        test-id="addGlobalNotification.messageInput"
      >
      </text-area-input>

      <div class="d-flex mb-2">
        <date-input
          :readonly="false"
          :label="DateFromCaption"
          :disabled="isLoading"
          :invalid="dateValidationFailed"
          :showHour="true"
          :showMinute="true"
          :showSecond="false"
          :date.sync="dateFrom"
          :labelWidth="labelWidth"
        >
        </date-input>

        <dark-button
          test-id="addGlobalNotification.todayButton"
          class="ml-2"
          :label="NowCaption"
          :disabled="isLoading"
          @clicked="setFromDateToNow"
        />
      </div>

      <div class="d-flex">
        <date-input
          :readonly="false"
          :label="DateToCaption"
          :disabled="isLoading"
          :invalid="dateValidationFailed"
          :showHour="true"
          :showMinute="true"
          :showSecond="false"
          :date.sync="dateTo"
          :labelWidth="labelWidth"
        >
        </date-input>

        <dark-button
          test-id="addGlobalNotification.tomorrowButton"
          class="ml-2"
          :label="TomorrowCaption"
          :disabled="isLoading"
          @clicked="setToDateToTomorrow"
        />
      </div>

      <fade-transition :maxHeight="28">
        <div v-if="validationErrors.length != 0" class="errors-container">
          <span class="mt-2">{{ validationErrors[0] }}</span>
        </div>
      </fade-transition>

      <div :class="['spinner', isLoading ? '' : 'hidden']"></div>
    </editor-content>

    <editor-footer>
      <template slot="buttons">
        <icon-button
          test-id="addGlobalNotification.addButton"
          :disabled="isLoading"
          iconName="fa-check"
          :text="AddCaption"
          @clicked="addButtonClicked"
        />
      </template>
    </editor-footer>
  </div>
</template>

<script lang="ts">
import en from '@/localization/en';
import { Component, Vue } from 'vue-property-decorator';

import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import FadeTransition from '@/components/presentation/FadeTransition.vue';
import IconButton from '../buttons/IconButton.vue';
import EditorFooter from '@/components/forms/base/EditorFooter.vue';
import EditorHeader from '@/components/forms/base/EditorHeader.vue';
import EditorContent from '@/components/forms/base/EditorContent.vue';
import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import { GlobalNotificationsModule } from '@/store/modules/globalNotificationsModule';
import DarkButton from '../buttons/DarkButton.vue';
import HeaderIconButton from '@/components/buttons/HeaderIconButton.vue';

@Component({
  name: 'add-global-notification',
  components: {
    HeaderIconButton,
    DatePicker: DatePicker,
    FadeTransition: FadeTransition,
    IconButton: IconButton,
    EditorHeader: EditorHeader,
    EditorContent: EditorContent,
    EditorFooter: EditorFooter,
    TextAreaInput: TextAreaInput,
    DateInput: DateInput,
    DarkButton: DarkButton,
  },
})
export default class AddGlobalNotification extends Vue {
  labelWidth: number = 100;

  //#region STATE
  private isLoading: boolean = false;

  private validationTimeoutHandle: number = -1;
  private textValidationFailed: boolean = false;
  private dateValidationFailed: boolean = false;
  private validationErrors: string[] = [];

  private text: string = '';

  private showFromTime: boolean = false;
  private showToTime: boolean = false;

  private from: Date = new Date(new Date().setSeconds(0));
  private to: Date = new Date(new Date().setSeconds(0));

  private get dateFrom(): Date {
    return this.from;
  }
  private set dateFrom(value: Date) {
    value.setSeconds(0);
    this.from = value;
  }

  private get dateTo(): Date {
    return this.to;
  }
  private set dateTo(value: Date) {
    value.setSeconds(0);
    this.to = value;
  }
  //#endregion

  //#region STATE LOGIC
  private toggleFromTime() {
    this.showFromTime = !this.showFromTime;
  }

  private toggleToTime() {
    this.showToTime = !this.showToTime;
  }

  private setFromDateToNow() {
    this.dateFrom = new Date();
  }

  private setToDateToTomorrow() {
    const now = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);

    this.dateTo = tomorrow;
  }

  private clearValidationErrors() {
    if (this.validationTimeoutHandle !== -1) {
      clearTimeout(this.validationTimeoutHandle);
    }

    this.validationErrors = [];
    this.dateValidationFailed = false;
    this.textValidationFailed = false;
    this.validationTimeoutHandle = -1;
  }

  private validate(): boolean {
    this.clearValidationErrors();

    if (this.text == null || this.text.length == 0) {
      this.validationErrors.push('Enter message text');
      this.textValidationFailed = true;
    } else if (this.from > this.to) {
      this.validationErrors.push('Final date has to be earlier than start date');
      this.dateValidationFailed = true;
    } else if (this.from.differenceInDays(this.to) < 0.99) {
      this.validationErrors.push('Minimum difference is one day. Select a larger range');
      this.dateValidationFailed = true;
    }

    const failed = this.validationErrors.length != 0;

    if (failed) {
      this.validationTimeoutHandle = window.setTimeout(() => {
        this.clearValidationErrors();
      }, 3500);
    }

    return !failed;
  }
  //#endregion

  //#region EVENTS
  private closeButtonClicked() {
    this.$emit('close-clicked');
  }

  private async addButtonClicked() {
    if (!this.validate()) {
      return;
    }

    this.from.setSeconds(0);
    this.from.setMilliseconds(0);

    this.to.setSeconds(0);
    this.to.setMilliseconds(0);

    this.isLoading = true;

    await GlobalNotificationsModule.CreateGlobalNotification([this.text, this.from.toJSON(), this.to.toJSON()]);

    this.isLoading = false;
  }
  //#endregion

  updated() {}

  //#region TRANSLATIONS
  private get AddGlobalNotificationCaption(): string {
    return en.addGlobalNotification.titleCase();
  }
  private get AddCaption(): string {
    return en.add.titleCase();
  }
  private get TextCaption(): string {
    return en.text.titleCase();
  }
  private get DateFromCaption(): string {
    return en.dateFrom.titleCase();
  }
  private get NowCaption(): string {
    return en.now.titleCase();
  }
  private get DateToCaption(): string {
    return en.dateTo.titleCase();
  }
  private get TomorrowCaption(): string {
    return en.tomorrow.titleCase();
  }
  //#endregion
}
</script>

<style lang="scss" scoped>
.add-global-notification {
  background: var(--editor-background);
  border-radius: 6px;

  .errors-container {
    display: flex;
    span {
      color: var(--editor-error-text);
      font-size: 14px;
    }
  }

  button {
    width: 100px;
  }
}
</style>
